import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Container,
  Center,
  Flex,
  HStack,
  VStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Button as ChakraButton,
} from '@chakra-ui/react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';

import RightSideActions from 'components/DevSandbox/RightSideActions';
import Icon from 'components/Icon';
import Link from 'components/Link';
import Logo from 'components/Logo';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { Project } from 'types/edgeFunctions';

import SubmitSolution from './SubmitSolution';

interface ISubmitProps {
  project: Project;
}

const Submit = ({ project }: ISubmitProps) => {
  const { pathname } = useLocation();
  const { isSmallScreen } = useMediaQueries();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isViaGithub = searchParams.get('via') === 'github';

  const backTo = pathname.replace('/submit', '');

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      width="100vw"
      height="100vh"
      backgroundColor="gray.900"
      zIndex="modal"
    >
      <VStack spacing={0} alignItems="stretch" height="100%">
        <Flex
          position="relative"
          height="60px"
          alignItems="center"
          px={4}
          backgroundColor="gray.900"
        >
          <Box mr="auto">
            <HStack spacing={0} flexGrow={1} justifyContent="flex-start" alignItems="center">
              <Logo isClickable size={24} rocketOnly />
              <Link to={backTo} color="white">
                <HStack spacing={1}>
                  <Icon type="BackArrow" />
                  <Text fontSize="small" fontWeight="regular" opacity="secondary">
                    Back to Code Editor
                  </Text>
                </HStack>
              </Link>
            </HStack>
          </Box>
          {!isSmallScreen && (
            <Box position="absolute" left="50%" transform="translateX(-50%)" textAlign="center">
              <Menu placement="bottom">
                <MenuButton
                  as={ChakraButton}
                  rightIcon={<ChevronDownIcon />}
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.100' }}
                  _active={{ bg: 'whiteAlpha.200' }}
                >
                  <HStack spacing={2}>
                    {isViaGithub ? (
                      <Icon type="Github" size={16} />
                    ) : (
                      <Icon type="SubmitCode" />
                    )}
                    <Text fontWeight="semibold">
                      {isViaGithub ? 'Submit via GitHub' : 'Code Editor'}
                    </Text>
                  </HStack>
                </MenuButton>
                <Portal>
                  <MenuList bg="gray.800" borderColor="whiteAlpha.200" minW="280px" py={0} overflow="hidden" zIndex="tooltip">
                    <MenuItem
                      bg={!isViaGithub ? 'whiteAlpha.100' : 'gray.800'}
                      _hover={{ bg: 'whiteAlpha.100' }}
                      onClick={() => navigate(`${pathname}`, { replace: true })}
                      py={3}
                      px={4}
                    >
                      <HStack spacing={3}>
                        <Box color="purple.300">
                          <Icon type="SubmitCode" />
                        </Box>
                        <VStack alignItems="flex-start" spacing={0}>
                          <Text fontWeight="semibold">Code Editor</Text>
                          <Text fontSize="micro" opacity="secondary" fontWeight="regular">
                            Submit code written in the built-in editor
                          </Text>
                        </VStack>
                      </HStack>
                    </MenuItem>
                    <MenuItem
                      bg={isViaGithub ? 'whiteAlpha.100' : 'gray.800'}
                      _hover={{ bg: 'whiteAlpha.100' }}
                      onClick={() => navigate(`${pathname}?via=github`, { replace: true })}
                      py={3}
                      px={4}
                      borderTop="1px solid"
                      borderColor="whiteAlpha.100"
                    >
                      <HStack spacing={3}>
                        <Box>
                          <Icon type="Github" size={20} />
                        </Box>
                        <VStack alignItems="flex-start" spacing={0}>
                          <Text fontWeight="semibold">Submit via GitHub</Text>
                          <Text fontSize="micro" opacity="secondary" fontWeight="regular">
                            Link your deployed website and repository
                          </Text>
                        </VStack>
                      </HStack>
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            </Box>
          )}
          <Box ml="auto">
            <RightSideActions />
          </Box>
        </Flex>
        <Box
          borderBottom="1px solid"
          borderColor="whiteAlpha.200"
          position="relative"
          zIndex={999}
        />
        <Container maxW="1400px">
          <Center height="90vh">
            <SubmitSolution project={project} />
          </Center>
        </Container>
      </VStack>
    </Box>
  );
};

export default Submit;
